import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Send, X, MessageCircle, Clock, Menu, Plus, Trash2, Pencil, Check, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { portalApi, usePortal } from "./PortalShell";

// Markdown styling tuned for a dark chat bubble.
const MD_COMPONENTS = {
  h1: (p) => <h3 className="text-base font-semibold text-white mt-3 mb-1" {...p} />,
  h2: (p) => <h4 className="text-sm font-semibold text-white mt-3 mb-1" {...p} />,
  h3: (p) => <div className="text-[11px] font-semibold text-orange-300 uppercase tracking-widest mt-3 mb-1" {...p} />,
  p: (p) => <p className="mb-2 leading-relaxed" {...p} />,
  ul: (p) => <ul className="list-disc pl-4 mb-2 space-y-1" {...p} />,
  ol: (p) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-semibold text-white" {...p} />,
  em: (p) => <em className="italic text-neutral-200" {...p} />,
  a: (p) => <a className="text-orange-300 underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
  hr: () => <hr className="border-neutral-700 my-2" />,
};

function MD({ text }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{text || ""}</ReactMarkdown>;
}

const INTRO_MESSAGE = `Hi 👋 I'm your **Fuelled Copilot** — your growth partner.

I'll help you:
- Reach the **next tier**
- Unlock **more commissions**
- Get **more free products**
- Book your **growth calls** (Pro / Pro Advanced / Legend)

Our marketing, branding, and sales teams are all here behind me to back you up. Ask me anything to get started.`;

const QUICK_PROMPTS = [
  "What's the fastest way to reach my next tier?",
  "How do I get more free products this month?",
  "Give me 3 client-pitch angles I can use this week",
  "How can I bring in more new customers?",
];

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

export default function PortalChatWidget() {
  const { profile } = usePortal();
  const [open, setOpen] = useState(false);
  // Threads
  const [threads, setThreads] = useState([]);       // [{id,title,updated_at,message_count}]
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [showThreadList, setShowThreadList] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  // Messages of active thread
  const [messages, setMessages] = useState([]);     // {role, content, created_at}
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef();

  // Fetch thread list on first open
  useEffect(() => {
    if (!open || threadsLoaded) return;
    portalApi
      .get("/affiliate/ai/threads")
      .then((r) => {
        const rows = r.data.threads || [];
        setThreads(rows);
        // Auto-select the most recent existing thread; if none exists we stay
        // on the null-thread state (a new thread will be created on first send).
        if (rows.length > 0) {
          setActiveThreadId(rows[0].id);
        } else {
          setShowThreadList(false);
        }
      })
      .catch(() => {})
      .finally(() => setThreadsLoaded(true));
  }, [open, threadsLoaded]);

  // Load messages for the active thread whenever it changes
  useEffect(() => {
    if (!open || !activeThreadId) {
      setMessages([]);
      setMessagesLoaded(true);
      return;
    }
    setMessagesLoaded(false);
    portalApi
      .get(`/affiliate/ai/threads/${activeThreadId}/messages`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => setMessages([]))
      .finally(() => setMessagesLoaded(true));
  }, [open, activeThreadId]);

  // Autoscroll to bottom whenever the list grows
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, sending]);

  const startNewChat = () => {
    setActiveThreadId(null);
    setMessages([]);
    setMessagesLoaded(true);
    setShowThreadList(false);
    setInput("");
  };

  const openThread = (id) => {
    setActiveThreadId(id);
    setShowThreadList(false);
  };

  const send = async (e, override) => {
    e?.preventDefault();
    const msg = (override ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", content: msg, created_at: now }]);
    setSending(true);
    try {
      const { data } = await portalApi.post("/affiliate/ai/chat", {
        message: msg,
        thread_id: activeThreadId,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, created_at: new Date().toISOString() }]);
      // Server may have created a new thread and auto-titled it — reflect that locally.
      if (data.thread_id && data.thread_id !== activeThreadId) {
        setActiveThreadId(data.thread_id);
      }
      // Refresh sidebar list so the newly-created / just-updated thread bubbles to top
      portalApi
        .get("/affiliate/ai/threads")
        .then((r) => setThreads(r.data.threads || []))
        .catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chat failed");
      // Roll back the optimistic user bubble
      setMessages((prev) => prev.slice(0, -1));
      setInput(msg);
    } finally {
      setSending(false);
    }
  };

  const deleteThread = async (id, title) => {
    if (!window.confirm(`Delete chat "${title || "New chat"}"? Messages inside will be lost.`)) return;
    try {
      await portalApi.delete(`/affiliate/ai/threads/${id}`);
      setThreads((prev) => prev.filter((t) => t.id !== id));
      if (activeThreadId === id) startNewChat();
      toast.success("Chat deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const saveRename = async (id) => {
    const title = (renameDraft || "").trim();
    if (!title) {
      setRenamingId(null);
      return;
    }
    try {
      await portalApi.patch(`/affiliate/ai/threads/${id}`, { title });
      setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, title } : t)));
      setRenamingId(null);
      setRenameDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    }
  };

  const firstName = profile?.name?.split(" ")[0] || "there";
  const activeThread = threads.find((t) => t.id === activeThreadId);
  const activeTitle = activeThread?.title || "New chat";

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="portal-chat-trigger"
          className="fixed bottom-20 right-4 sm:bottom-5 sm:right-5 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-orange-500 to-red-600 shadow-[0_10px_40px_-8px_rgba(249,115,22,0.7)] hover:scale-105 transition-transform flex items-center justify-center group"
          aria-label="Open Fuelled Copilot"
        >
          <Sparkles className="h-6 w-6 text-white group-hover:rotate-12 transition-transform" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 animate-pulse" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="portal-chat-panel"
          className="fixed inset-x-0 bottom-0 top-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[600px] sm:w-[400px] sm:max-h-[85vh] z-50 sm:rounded-2xl bg-neutral-950 border border-neutral-800 flex flex-col shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-3 border-b border-neutral-800 bg-gradient-to-r from-orange-950/60 to-red-950/40">
            {showThreadList ? (
              <button
                onClick={() => setShowThreadList(false)}
                data-testid="portal-chat-threads-back"
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-neutral-800 shrink-0"
                aria-label="Back to chat"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => setShowThreadList(true)}
                data-testid="portal-chat-threads-open"
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-neutral-800 shrink-0"
                aria-label="Open your chats"
              >
                <Menu className="h-4 w-4" />
              </button>
            )}
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight truncate" data-testid="portal-chat-active-title">
                  {showThreadList ? "Your chats" : activeTitle}
                </div>
                {!showThreadList && (
                  <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Fuelled Copilot online
                  </div>
                )}
              </div>
            </div>
            {!showThreadList && (
              <button
                onClick={startNewChat}
                data-testid="portal-chat-new"
                title="Start a new chat"
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-neutral-800 shrink-0"
                aria-label="New chat"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              data-testid="portal-chat-close"
              className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-neutral-800 shrink-0"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Thread list */}
          {showThreadList && (
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5" data-testid="portal-chat-threads-list">
              <button
                onClick={startNewChat}
                data-testid="portal-chat-threads-new"
                className="w-full flex items-center gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 px-3 py-2.5 text-sm font-medium text-orange-200 mb-2"
              >
                <Plus className="h-4 w-4" /> Start a new chat
              </button>
              {threads.length === 0 && (
                <div className="text-[12px] text-neutral-500 italic px-1 py-4 text-center">
                  No chats yet. Tap "Start a new chat" to begin.
                </div>
              )}
              {threads.map((t) => {
                const isActive = t.id === activeThreadId;
                const isRenaming = renamingId === t.id;
                return (
                  <div
                    key={t.id}
                    data-testid={`portal-chat-thread-row-${t.id}`}
                    className={`rounded-xl px-3 py-2 flex items-center gap-2 ${isActive ? "bg-orange-500/10 border border-orange-500/30" : "bg-neutral-900 border border-neutral-800 hover:bg-neutral-800/60"}`}
                  >
                    {isRenaming ? (
                      <input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveRename(t.id);
                          if (e.key === "Escape") setRenamingId(null);
                        }}
                        data-testid={`portal-chat-thread-rename-input-${t.id}`}
                        className="flex-1 bg-neutral-950 border border-neutral-700 rounded px-2 py-1 text-sm outline-none focus:border-orange-500"
                      />
                    ) : (
                      <button
                        onClick={() => openThread(t.id)}
                        data-testid={`portal-chat-thread-open-${t.id}`}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="text-sm text-neutral-100 truncate">{t.title || "New chat"}</div>
                        <div className="text-[10px] text-neutral-500 truncate">
                          {(t.message_count || 0)} message{(t.message_count || 0) === 1 ? "" : "s"} · {fmtWhen(t.updated_at)}
                        </div>
                      </button>
                    )}
                    {isRenaming ? (
                      <>
                        <button
                          onClick={() => saveRename(t.id)}
                          data-testid={`portal-chat-thread-rename-save-${t.id}`}
                          className="h-7 w-7 rounded-full bg-orange-500 hover:bg-orange-400 text-white flex items-center justify-center"
                          aria-label="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setRenamingId(null)}
                          className="h-7 w-7 rounded-full bg-neutral-800 hover:bg-neutral-700 text-neutral-300 flex items-center justify-center"
                          aria-label="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setRenamingId(t.id);
                            setRenameDraft(t.title || "");
                          }}
                          data-testid={`portal-chat-thread-rename-${t.id}`}
                          className="h-7 w-7 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 flex items-center justify-center"
                          aria-label="Rename"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => deleteThread(t.id, t.title)}
                          data-testid={`portal-chat-thread-delete-${t.id}`}
                          className="h-7 w-7 rounded-full text-neutral-500 hover:text-red-400 hover:bg-neutral-800 flex items-center justify-center"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Messages */}
          {!showThreadList && (
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
              {/* Intro card — always shown as the first message */}
              <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-3.5 text-sm text-neutral-100">
                <MD text={INTRO_MESSAGE.replace("Hi 👋", `Hi ${firstName} 👋`)} />
              </div>

              {messagesLoaded && messages.map((m, i) => (
                <div key={i} data-testid={`portal-chat-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" ? (
                    <div className="max-w-[90%] rounded-2xl bg-neutral-900 border border-neutral-800 px-3.5 py-2.5 text-neutral-100">
                      <MD text={m.content} />
                    </div>
                  ) : (
                    <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 bg-orange-500 text-white whitespace-pre-wrap">{m.content}</div>
                  )}
                </div>
              ))}

              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-3.5 py-2.5 bg-neutral-900 border border-neutral-800 text-neutral-400 italic flex items-center gap-2">
                    <Clock className="h-3 w-3 animate-pulse" /> Thinking…
                  </div>
                </div>
              )}

              {/* Quick prompts — show only on a fresh new-chat state */}
              {messagesLoaded && messages.length === 0 && !sending && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" /> Try asking
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {QUICK_PROMPTS.map((q, i) => (
                      <button
                        key={i}
                        data-testid={`portal-chat-quick-${i}`}
                        onClick={(e) => send(e, q)}
                        className="text-left text-xs rounded-xl border border-neutral-800 bg-neutral-900/60 hover:bg-neutral-800 hover:border-orange-500/40 px-3 py-2 text-neutral-200 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Composer — hidden while browsing the thread list */}
          {!showThreadList && (
            <form onSubmit={send} className="border-t border-neutral-800 p-2.5 flex gap-2 bg-neutral-950">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about tiers, commissions, growth ideas…"
                className="flex-1 h-10 rounded-xl bg-neutral-900 border border-neutral-800 px-3.5 text-sm outline-none focus:border-orange-500/60"
                disabled={sending}
                data-testid="portal-chat-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="portal-chat-send"
                className="h-10 w-10 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-40 flex items-center justify-center shrink-0"
                aria-label="Send"
              >
                <Send className="h-4 w-4 text-white" />
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
